import React, { useMemo, useContext } from 'react';
import { compose } from 'recompose';
import { css } from '@emotion/react';
import { useClientUser } from '@/hooks';
import TariffLibrary from '@/components/Account/TariffLibrary';
import { ALGO_TYPES } from '@/components/Account/TariffTable';

import { Context, ContextProvider } from './store';
import { Page, Title } from './blocks';
import OrgHeader from './OrgHeader';

const AdminTariffs = () => {
    const { isCorporateAdmin, organization } = useClientUser();
    const { state, loading, callbacks } = useContext(Context);

    const algoTypes = useMemo(
        () => ALGO_TYPES.ORDER.filter((type) => type.public).sort((a, b) => a.label.localeCompare(b.label)),
        []
    );

    const clientsById = useMemo(
        () => Object.fromEntries((state.admin_clients || []).map((c) => [c.client_id, c])),
        [state.admin_clients]
    );

    const ownerLabel = (tariff) => {
        if (!tariff.client_id) return 'Org-level (shared)';
        const client = clientsById[tariff.client_id];
        const name = client?.business_name || 'Account';
        const roles = client?.user?.roles || {};
        const prefix = roles['CARRIER'] ? 'Carrier: ' : roles['SHIPPER'] ? 'Shipper: ' : '';
        return `${prefix}${name}`;
    };

    return (
        <Page>
            {/* Onward admins manage global tariffs and have no org to show. */}
            {isCorporateAdmin && organization?.name ? <OrgHeader name={organization.name} /> : <Title>Tariffs</Title>}
            <TariffLibrary
                tariffs={state.tariffs}
                loading={loading.init || loading.delete}
                algoTypeOptions={algoTypes}
                onCreate={callbacks.createTariff}
                onDuplicate={callbacks.duplicateTariff}
                onDelete={callbacks.deleteTariff}
                showAssignments
                renderRowMeta={(tariff) => (
                    <>
                        <span>{tariff.algo_type}</span>
                        <span
                            css={css`
                                color: ${tariff.client_id ? '#5b5b5b' : '#1b8a5a'};
                            `}
                        >
                            {ownerLabel(tariff)}
                        </span>
                    </>
                )}
            />
        </Page>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withContext)(AdminTariffs);
